import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../../i18n';

export interface FastFingersPlayerProps {
  successEmoji: string;
  wrongEmojis: string[];
  totalCount: number;
  successCount: number;
  successPoints: number;
  wrongPenalty: number;
  timeLimit: number;
  /** Called once to submit the final score (local timer or round-end backup). */
  onSubmit: (answer: string, points?: number) => void;
  /** Called every time the score changes (for parent to track latest score in overlay). */
  onScoreChange?: (score: number) => void;
  /** Called periodically (~2s) so server has this player's score before round ends; ensures all players get points. */
  onPeriodicSubmit?: (points: number) => void;
  disabled?: boolean;
}

interface ActiveEmoji {
  slotId: number;
  emoji: string;
  isSuccess: boolean;
  spawnTime: number;
}

/**
 * Arcade-style "Whack-a-Mole" player view.
 * Emojis appear one by one at random positions, players must tap them quickly.
 * Success emojis give points, wrong emojis cost points.
 * Emojis disappear after a few seconds if not tapped.
 *
 * Submits the final score once: either via the local timer (fires at timeLimit)
 * or as a backup when `disabled` becomes true (round ended from server).
 * No periodic submissions — single submit at the end.
 */
export const FastFingersPlayer: React.FC<FastFingersPlayerProps> = ({
  successEmoji,
  wrongEmojis,
  totalCount,
  successCount,
  successPoints,
  wrongPenalty,
  timeLimit,
  onSubmit,
  onScoreChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  const [activeEmojis, setActiveEmojis] = useState<Map<number, ActiveEmoji>>(new Map());
  const [submitted, setSubmitted] = useState(false);
  const submitRef = useRef(false);
  // Stable ref for onSubmit so the timer doesn't reset on every parent re-render
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => { onSubmitRef.current = onSubmit; }, [onSubmit]);

  const cols = 8;
  const rows = 8;
  const totalSlots = cols * rows;

  // Track slots tapped recently so we avoid spawning in the same place right after a tap
  const recentlyTappedRef = useRef<Map<number, number>>(new Map());
  const TAP_COOLDOWN_MS = 400;

  // Build the shuffled emoji queue once on mount (stable ref, no re-renders)
  const emojiQueueRef = useRef<Array<{ emoji: string; isSuccess: boolean }> | null>(null);
  if (emojiQueueRef.current === null) {
    const queue: Array<{ emoji: string; isSuccess: boolean }> = [];
    for (let i = 0; i < successCount; i++) {
      queue.push({ emoji: successEmoji, isSuccess: true });
    }
    for (let i = successCount; i < totalCount; i++) {
      queue.push({ emoji: wrongEmojis[Math.floor(Math.random() * wrongEmojis.length)], isSuccess: false });
    }
    for (let i = queue.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [queue[i], queue[j]] = [queue[j], queue[i]];
    }
    emojiQueueRef.current = queue;
  }

  // ── Spawn emojis one-by-one using a single chain of setTimeout ──
  // Uses functional setActiveEmojis so we never need activeEmojis in the dep array.
  // IMPORTANT: No side effects (setTimeout) inside the state updater — only outside.
  useEffect(() => {
    if (disabled || submitted) return;

    const queue = emojiQueueRef.current!;
    let queueIndex = 0;
    let cancelled = false;
    let pendingTimeout: ReturnType<typeof setTimeout> | null = null;

    const spawnNext = () => {
      if (cancelled || queueIndex >= queue.length) return;

      const idx = queueIndex;
      let spawned = false;

      const now = Date.now();
      const recent = recentlyTappedRef.current;
      for (const [slot, ts] of Array.from(recent.entries())) {
        if (now - ts > TAP_COOLDOWN_MS) recent.delete(slot);
      }

      setActiveEmojis((prev) => {
        // Find available slot: not occupied and not recently tapped
        const occupied = new Set(prev.keys());
        const available: number[] = [];
        for (let i = 0; i < totalSlots; i++) {
          if (occupied.has(i)) continue;
          const tappedAt = recent.get(i);
          if (tappedAt != null && now - tappedAt < TAP_COOLDOWN_MS) continue;
          available.push(i);
        }
        if (available.length === 0) {
          // All slots full — signal retry (no side effects here)
          return prev;
        }
        spawned = true;
        const slot = available[Math.floor(Math.random() * available.length)];
        const next = new Map(prev);
        next.set(slot, {
          slotId: slot,
          emoji: queue[idx].emoji,
          isSuccess: queue[idx].isSuccess,
          spawnTime: Date.now(),
        });
        return next;
      });

      if (cancelled) return;

      if (spawned) {
        queueIndex++;
        // Schedule next spawn (150–280ms for a livelier pace)
        const delay = 150 + Math.random() * 130;
        pendingTimeout = setTimeout(spawnNext, delay);
      } else {
        // Grid full — retry shortly
        pendingTimeout = setTimeout(spawnNext, 200);
      }
    };

    // Wait 5s before spawning — matches the bonus intro duration so the player
    // sees the rules/instructions before items start appearing.
    pendingTimeout = setTimeout(spawnNext, 5000);

    return () => {
      cancelled = true;
      if (pendingTimeout) clearTimeout(pendingTimeout);
    };
  }, [disabled, submitted, totalSlots]);

  // Auto-remove emojis if not tapped: wrong after 3s, success after 4s
  useEffect(() => {
    if (disabled || submitted) return;

    const interval = setInterval(() => {
      const now = Date.now();
      setActiveEmojis((prev) => {
        let changed = false;
        const next = new Map(prev);
        const entries = Array.from(prev.entries());
        for (let i = 0; i < entries.length; i++) {
          const [slotId, emoji] = entries[i];
          const maxLifetime = emoji.isSuccess ? 4000 : 3000;
          if (now - emoji.spawnTime > maxLifetime) {
            next.delete(slotId);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [disabled, submitted]);

  const handleTap = useCallback(
    (slotId: number) => {
      if (disabled || submitted) return;

      setActiveEmojis((prev) => {
        const emoji = prev.get(slotId);
        if (!emoji) return prev;

        // Avoid spawning in same slot shortly after tap
        recentlyTappedRef.current.set(slotId, Date.now());

        // Update score
        const pointChange = emoji.isSuccess ? successPoints : -wrongPenalty;
        setScore((prevScore) => {
          const newScore = prevScore + pointChange;
          scoreRef.current = newScore;
          onScoreChange?.(newScore);
          return newScore;
        });

        const next = new Map(prev);
        next.delete(slotId);
        return next;
      });
    },
    [disabled, submitted, successPoints, wrongPenalty, onScoreChange]
  );

  // Auto-submit when local timer fires (before server grace period ends)
  useEffect(() => {
    if (submitRef.current || disabled) return;

    const timer = setTimeout(() => {
      if (!submitRef.current) {
        submitRef.current = true;
        setSubmitted(true);
        onSubmitRef.current('time-up', scoreRef.current);
      }
    }, timeLimit);

    return () => clearTimeout(timer);
  }, [timeLimit, disabled]);

  // Backup: when disabled becomes true (round ended from server), submit if not yet done
  useEffect(() => {
    if (disabled && !submitRef.current) {
      submitRef.current = true;
      setSubmitted(true);
      onSubmitRef.current('time-up', scoreRef.current);
    }
  }, [disabled]);

  const gridSize = Math.min(420, window.innerWidth - 40);
  const cellSize = gridSize / cols;

  return (
    <div
      style={{
        padding: '1rem 0.5rem',
        maxWidth: 480,
        margin: '0 auto',
        textAlign: 'center',
      }}
    >
      {/* Score display */}
      <div
        style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          marginBottom: '1rem',
          color: score >= 0 ? '#34D399' : '#EF4444',
        }}
      >
        {t('bonus.player.scoreLabel', { score })}
      </div>

      {/* Emoji grid */}
      <div
        style={{
          position: 'relative',
          width: gridSize,
          height: gridSize,
          margin: '0 auto',
          background: 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.08) 0%, rgba(var(--color-secondary-rgb), 0.14) 100%)',
          borderRadius: '12px',
          border: '2px solid rgba(var(--color-secondary-rgb), 0.28)',
          opacity: disabled ? 0.5 : 1,
          transition: 'opacity 0.3s',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        {Array.from({ length: totalSlots }, (_, i) => {
          const row = Math.floor(i / cols);
          const col = i % cols;
          const activeEmoji = activeEmojis.get(i);

          return (
            <button
              key={i}
              type="button"
              onClick={() => handleTap(i)}
              disabled={disabled || submitted || !activeEmoji}
              style={{
                position: 'absolute',
                left: `${(col / cols) * 100}%`,
                top: `${(row / rows) * 100}%`,
                width: cellSize,
                height: cellSize,
                fontSize: cellSize * 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                background: 'transparent',
                cursor: activeEmoji && !disabled && !submitted ? 'pointer' : 'default',
                opacity: activeEmoji ? 1 : 0,
                transition: 'opacity 0.15s ease, transform 0.1s ease',
                transform: activeEmoji ? 'scale(1)' : 'scale(0.5)',
                padding: 0,
              }}
            >
              {activeEmoji?.emoji ?? ''}
            </button>
          );
        })}
      </div>
    </div>
  );
};
